import { useState, FormEvent } from "react";

export default function BookingSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    setLoading(true);
    try {
      await fetch("https://readdy.ai/api/form/d7csbd9noju8nk5a11eg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-amber-400 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-amber-400 translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-14 items-start">
          {/* Left */}
          <div className="flex-1 text-white">
            <span className="inline-block px-4 py-1.5 rounded-full border border-amber-400/40 text-amber-400 text-xs font-semibold tracking-widest uppercase mb-6">
              Get Started Today
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold mb-5 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Book Your Free Consultation
            </h2>
            <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-md">
              Talk to one of our expert counselors — completely free. We&apos;ll assess your goals, review your current scores, and build a personalized roadmap to your dream university.
            </p>

            <div className="flex flex-col gap-5">
              {[
                { icon: "ri-checkbox-circle-line", text: "Personalized assessment of your academic profile" },
                { icon: "ri-checkbox-circle-line", text: "Custom SAT/ACT score improvement plan" },
                { icon: "ri-checkbox-circle-line", text: "University shortlist tailored to your goals" },
                { icon: "ri-checkbox-circle-line", text: "No commitment — 100% free session" },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-amber-400 text-lg`}></i>
                  </div>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="w-9 h-9 rounded-full border-2 border-gray-900 overflow-hidden bg-gray-600">
                    <img
                      src={`https://public.readdy.ai/ai/img_res/5155610a1e0e1dc63b3e3d53266d3e7c.jpg${n}&orientation=squarish`}
                      alt="student"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-amber-400 text-xs"></i>
                  ))}
                </div>
                <p className="text-white/50 text-xs mt-0.5">Trusted by 2,400+ students &amp; families</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="w-full lg:w-[480px] bg-white rounded-3xl p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mb-4">
                  <i className="ri-check-line text-emerald-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  You&apos;re All Set!
                </h3>
                <p className="text-gray-500 text-sm">
                  Thank you! One of our counselors will reach out within 24 hours to schedule your free consultation.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Free Consultation
                </h3>
                <p className="text-gray-400 text-sm mb-6">Fill in your details and we&apos;ll be in touch within 24 hours.</p>

                <form
                  data-readdy-form
                  id="free-consultation-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Student&apos;s First Name</label>
                      <input
                        name="first_name"
                        type="text"
                        required
                        placeholder="e.g. Olivia"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Last Name</label>
                      <input
                        name="last_name"
                        type="text"
                        required
                        placeholder="e.g. Harrison"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Parent / Guardian Email</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="parent@email.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Student&apos;s Current Grade</label>
                    <select
                      name="grade"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select grade</option>
                      <option value="Grade 6">Grade 6 (Middle School)</option>
                      <option value="Grade 7">Grade 7</option>
                      <option value="Grade 8">Grade 8</option>
                      <option value="Grade 9">Grade 9 (Freshman)</option>
                      <option value="Grade 10">Grade 10 (Sophomore)</option>
                      <option value="Grade 11">Grade 11 (Junior)</option>
                      <option value="Grade 12">Grade 12 (Senior)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Service of Interest</label>
                    <select
                      name="service"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="SAT/ACT Test Prep">SAT / ACT Test Prep</option>
                      <option value="1-on-1 Tutoring">1-on-1 Tutoring</option>
                      <option value="Essay Writing Coaching">Essay Writing Coaching</option>
                      <option value="College Application Strategy">College Application Strategy</option>
                      <option value="University Admissions Consulting">University Admissions Consulting</option>
                      <option value="Scholarship Guidance">Scholarship Guidance</option>
                      <option value="Online Group Classes">Online Group Classes</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Tell us about your goals <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      name="goals"
                      rows={3}
                      maxLength={500}
                      placeholder="e.g. My daughter wants to apply to Harvard and needs SAT prep..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {loading ? "Submitting..." : "Book My Free Consultation"}
                  </button>

                  <p className="text-center text-xs text-gray-400">
                    No spam, ever. We respect your privacy.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
